/** Styles for VectorCharts enc-v2 MVT layers (Leaflet.VectorGrid). */

const HIDE = [];

const LAND = {
  fill: true,
  fillColor: '#e8dcc8',
  fillOpacity: 1,
  stroke: true,
  color: '#b8a888',
  weight: 0.5,
  opacity: 0.7,
};

const WATER = {
  fill: true,
  fillColor: '#c5dff0',
  fillOpacity: 0.9,
  stroke: true,
  color: '#7eb0cc',
  weight: 0.4,
  opacity: 0.6,
};

const LINE = (color = '#4a7088', weight = 1) => ({
  fill: false,
  stroke: true,
  color,
  weight,
  opacity: 0.85,
});

const POINT = (fillColor, radius = 4) => ({
  fill: true,
  fillColor,
  fillOpacity: 1,
  stroke: true,
  color: '#1a3040',
  weight: 1,
  radius,
});

function depareStyle(properties) {
  const d1 = parseFloat(properties?.DRVAL1 ?? properties?.drval1);
  const d2 = parseFloat(properties?.DRVAL2 ?? properties?.drval2);
  const depth = Number.isFinite(d1) ? d1 : Number.isFinite(d2) ? d2 : null;

  let fillColor = '#9ec9e8';
  if (depth !== null) {
    if (depth <= 0) fillColor = '#d4eef8';
    else if (depth < 2) fillColor = '#8fd0ea';
    else if (depth < 5) fillColor = '#6db8dc';
    else if (depth < 10) fillColor = '#529fc8';
    else if (depth < 20) fillColor = '#3d86b0';
    else fillColor = '#2d6d96';
  }

  return {
    fill: true,
    fillColor,
    fillOpacity: 0.88,
    stroke: true,
    color: '#1e5570',
    weight: 0.6,
    opacity: 0.75,
  };
}

function depcntStyle() {
  return {
    fill: false,
    stroke: true,
    color: '#1a5070',
    weight: 1.4,
    opacity: 0.95,
  };
}

function fairwayStyle() {
  return {
    fill: true,
    fillColor: '#b8e4f5',
    fillOpacity: 0.75,
    stroke: true,
    color: '#3d8aaa',
    weight: 0.8,
    opacity: 0.8,
  };
}

function hazardStyle() {
  return {
    fill: true,
    fillColor: '#e8a0b0',
    fillOpacity: 0.7,
    stroke: true,
    color: '#c04060',
    weight: 1,
    opacity: 0.9,
  };
}

function restrictedStyle() {
  return {
    fill: true,
    fillColor: '#f0d890',
    fillOpacity: 0.55,
    stroke: true,
    color: '#a08030',
    weight: 0.8,
    opacity: 0.8,
    dashArray: '4,3',
  };
}

const LAND_LAYERS = new Set([
  's57_lndare',
  's57_lndrgn',
  's57_buaare',
  's57_buisgl',
  's57_lndelv',
  's57_lndmrk',
  's57_ponton',
  's57_hulkes',
  's57_bridge',
  's57_flodoc',
  's57_pylons',
  's57_siltnk',
  's57_slcons',
  's57_slotop',
  's57_tunnel',
  'basemap_ne_10m_land',
  'basemap_ne_10m_urban_areas',
]);

const WATER_LAYERS = new Set([
  's57_lakare',
  's57_unsare',
  's57_canals',
  's57_rivers',
  's57_prdare',
  's57_pipare',
  's57_resare',
  's57_ctnare',
  's57_achare',
  's57_admare',
  's57_airare',
  's57_cblare',
  's57_pipohd',
  's57_pipsol',
  's57_hrbfac',
  's57_morfac',
  's57_gatcon',
  's57_berths',
]);

const LINE_LAYERS = new Set([
  's57_coalne',
  's57_depcnt',
  's57_navlne',
  's57_rectrc',
  's57_cblohd',
  's57_cblsub',
  's57_sndwav',
  's57_wattur',
  's57_magvar',
  's57_rdosta',
]);

const POINT_LAYERS = {
  s57_lights: () => POINT('#ffd700', 5),
  s57_boylat: () => POINT('#ff6600', 5),
  s57_boyspp: () => POINT('#ff6600', 4),
  s57_bcnlat: () => POINT('#00aa44', 5),
  s57_bcnspp: () => POINT('#00aa44', 4),
  s57_pilpnt: () => POINT('#888888', 3),
  s57_daymar: () => POINT('#ffcc00', 4),
  s57_obstrn: () => POINT('#cc2244', 4),
  s57_uwtroc: () => POINT('#cc2244', 4),
  s57_wrecks: () => POINT('#993366', 4),
  s57_wedklp: () => POINT('#cc8844', 4),
  s57_ofsplf: () => POINT('#cc8844', 3),
};

const HIDDEN_LAYERS = new Set([
  'meta_s57_bounds',
  'meta_no_data',
  'meta_overscale',
  's57_m_covr',
  's57_m_covr_ctr',
]);

const ALL_LAYERS = [
  'basemap_ne_10m_bathymetry_',
  'basemap_ne_10m_land',
  'basemap_ne_10m_rivers_north_america',
  'basemap_ne_10m_urban_areas',
  'meta_no_data',
  'meta_overscale',
  'meta_s57_bounds',
  's57_achare', 's57_achare_ctr',
  's57_admare', 's57_admare_ctr',
  's57_airare', 's57_airare_ctr',
  's57_bcnlat', 's57_bcnspp', 's57_berths',
  's57_boylat', 's57_boyspp',
  's57_bridge', 's57_bridge_ctr',
  's57_buaare', 's57_buaare_ctr',
  's57_buisgl', 's57_buisgl_ctr',
  's57_canals', 's57_canals_ctr',
  's57_cblare', 's57_cblare_ctr',
  's57_cblohd', 's57_cblohd_ctr',
  's57_cblsub', 's57_cblsub_ctr',
  's57_coalne', 's57_coalne_ctr',
  's57_ctnare', 's57_ctnare_ctr',
  's57_daymar',
  's57_depare', 's57_depare_ctr',
  's57_depcnt', 's57_depcnt_ctr',
  's57_fairwy', 's57_fairwy_ctr',
  's57_flodoc', 's57_flodoc_ctr',
  's57_gatcon', 's57_gatcon_ctr',
  's57_hrbfac',
  's57_hulkes', 's57_hulkes_ctr',
  's57_lakare', 's57_lakare_ctr',
  's57_lights',
  's57_lndare', 's57_lndare_ctr',
  's57_lndelv', 's57_lndmrk',
  's57_lndrgn', 's57_lndrgn_ctr',
  's57_m_covr', 's57_m_covr_ctr',
  's57_magvar', 's57_magvar_ctr',
  's57_morfac',
  's57_navlne', 's57_navlne_ctr',
  's57_obstrn', 's57_obstrn_ctr',
  's57_ofsplf',
  's57_pilpnt',
  's57_pipare', 's57_pipare_ctr',
  's57_pipohd', 's57_pipohd_ctr',
  's57_pipsol', 's57_pipsol_ctr',
  's57_ponton', 's57_ponton_ctr',
  's57_prdare', 's57_prdare_ctr',
  's57_pylons', 's57_pylons_ctr',
  's57_rdosta',
  's57_rectrc', 's57_rectrc_ctr',
  's57_resare', 's57_resare_ctr',
  's57_rivers', 's57_rivers_ctr',
  's57_siltnk', 's57_siltnk_ctr',
  's57_slcons', 's57_slcons_ctr',
  's57_slotop', 's57_slotop_ctr',
  's57_sndwav',
  's57_tunnel', 's57_tunnel_ctr',
  's57_unsare', 's57_unsare_ctr',
  's57_uwtroc',
  's57_wattur',
  's57_wedklp',
  's57_wrecks', 's57_wrecks_ctr',
];

function styleForLayer(name) {
  if (HIDDEN_LAYERS.has(name)) return HIDE;
  if (name === 'basemap_ne_10m_bathymetry_') {
    return {
      fill: true,
      fillColor: '#7eb8d8',
      fillOpacity: 0.65,
      stroke: true,
      color: '#4a8098',
      weight: 0.5,
      opacity: 0.6,
    };
  }
  if (name === 'basemap_ne_10m_rivers_north_america') return LINE('#6aafd0', 1.2);
  if (name === 's57_depare') return depareStyle;
  if (name === 's57_depcnt' || name === 's57_depcnt_ctr') return depcntStyle;
  if (name === 's57_fairwy' || name === 's57_fairwy_ctr') return fairwayStyle;
  if (name === 's57_obstrn' || name === 's57_obstrn_ctr' || name === 's57_uwtroc' || name === 's57_wrecks' || name === 's57_wrecks_ctr') {
    return hazardStyle;
  }
  if (name === 's57_resare' || name === 's57_resare_ctr' || name === 's57_ctnare' || name === 's57_ctnare_ctr') {
    return restrictedStyle;
  }
  if (POINT_LAYERS[name]) return POINT_LAYERS[name];
  if (name.endsWith('_ctr')) {
    const base = name.replace(/_ctr$/, '');
    if (base === 's57_depcnt') return depcntStyle;
    if (LAND_LAYERS.has(base)) return LINE('#b8a888', 0.8);
    return LINE('#5080a0', 0.7);
  }
  if (LAND_LAYERS.has(name)) return LAND;
  if (WATER_LAYERS.has(name)) return WATER;
  if (LINE_LAYERS.has(name)) return LINE();
  return WATER;
}

/** Object keyed by MVT source-layer name (required by Leaflet.VectorGrid). */
export const VECTOR_CHARTS_LAYER_STYLES = Object.fromEntries(
  ALL_LAYERS.map((name) => [name, styleForLayer(name)])
);
